export type ToolDomain =
  | "auth"
  | "mail"
  | "calendar"
  | "contacts"
  | "files"
  | "chats"
  | "teams"
  | "meetings"
  | "users"
  | "groups"
  | "planner"
  | "onenote"
  | "todo"
  | "query"
  | "rag"

export type TransportMode = "stdio" | "httpStream"

export type ToolMeta = {
  readonly name: string
  readonly domain: ToolDomain
  readonly readOnly: boolean
  readonly orgOnly: boolean
  readonly transportOnly?: TransportMode
}

export const PRESETS: Record<string, ReadonlyArray<ToolDomain>> = {
  personal: ["mail", "calendar", "contacts", "todo", "files", "onenote"],
  collaboration: ["chats", "teams", "meetings", "planner", "groups"],
  productivity: ["mail", "calendar", "todo"],
  rag: ["rag", "files", "query"],
  all: [
    "auth",
    "mail",
    "calendar",
    "contacts",
    "files",
    "chats",
    "teams",
    "meetings",
    "users",
    "groups",
    "planner",
    "onenote",
    "todo",
    "query",
    "rag",
  ],
}

export const TOOL_METADATA: ReadonlyArray<ToolMeta> = [
  // Auth
  { name: "get_auth_status", domain: "auth", readOnly: true, orgOnly: false },
  { name: "list_accounts", domain: "auth", readOnly: true, orgOnly: false },
  { name: "switch_account", domain: "auth", readOnly: false, orgOnly: false },
  { name: "set_access_token", domain: "auth", readOnly: false, orgOnly: false },
  // Mail
  { name: "list_messages", domain: "mail", readOnly: true, orgOnly: false },
  { name: "get_message", domain: "mail", readOnly: true, orgOnly: false },
  { name: "list_mail_folders", domain: "mail", readOnly: true, orgOnly: false },
  { name: "move_message", domain: "mail", readOnly: false, orgOnly: false },
  { name: "list_attachments", domain: "mail", readOnly: true, orgOnly: false },
  { name: "batch_move_messages", domain: "mail", readOnly: false, orgOnly: false },
  { name: "send_message", domain: "mail", readOnly: false, orgOnly: false },
  { name: "send_reply", domain: "mail", readOnly: false, orgOnly: false },
  { name: "send_reply_all", domain: "mail", readOnly: false, orgOnly: false },
  { name: "send_forward", domain: "mail", readOnly: false, orgOnly: false },
  { name: "search_messages", domain: "mail", readOnly: true, orgOnly: false },
  { name: "create_draft", domain: "mail", readOnly: false, orgOnly: false },
  { name: "create_reply_draft", domain: "mail", readOnly: false, orgOnly: false },
  { name: "create_reply_all_draft", domain: "mail", readOnly: false, orgOnly: false },
  { name: "create_forward_draft", domain: "mail", readOnly: false, orgOnly: false },
  { name: "send_draft", domain: "mail", readOnly: false, orgOnly: false },
  // Calendar
  { name: "list_events", domain: "calendar", readOnly: true, orgOnly: false },
  { name: "list_calendar_view", domain: "calendar", readOnly: true, orgOnly: false },
  { name: "find_meeting_availability", domain: "calendar", readOnly: true, orgOnly: false },
  { name: "get_event", domain: "calendar", readOnly: true, orgOnly: false },
  { name: "create_event", domain: "calendar", readOnly: false, orgOnly: false },
  { name: "update_event", domain: "calendar", readOnly: false, orgOnly: false },
  { name: "delete_event", domain: "calendar", readOnly: false, orgOnly: false },
  // Contacts
  { name: "list_contacts", domain: "contacts", readOnly: true, orgOnly: false },
  { name: "get_contact", domain: "contacts", readOnly: true, orgOnly: false },
  { name: "create_contact", domain: "contacts", readOnly: false, orgOnly: false },
  { name: "search_contacts", domain: "contacts", readOnly: true, orgOnly: false },
  // Files
  { name: "list_drive_items", domain: "files", readOnly: true, orgOnly: false },
  { name: "get_drive_item", domain: "files", readOnly: true, orgOnly: false },
  { name: "search_files", domain: "files", readOnly: true, orgOnly: false },
  { name: "download_file", domain: "files", readOnly: true, orgOnly: false },
  { name: "create_folder", domain: "files", readOnly: false, orgOnly: false },
  { name: "upload_file", domain: "files", readOnly: false, orgOnly: false },
  { name: "get_upload_config", domain: "files", readOnly: false, orgOnly: false, transportOnly: "httpStream" },
  { name: "upload_file_from_path", domain: "files", readOnly: false, orgOnly: false, transportOnly: "stdio" },
  // SharePoint
  { name: "list_sites", domain: "files", readOnly: true, orgOnly: true },
  { name: "get_site", domain: "files", readOnly: true, orgOnly: true },
  { name: "list_site_drives", domain: "files", readOnly: true, orgOnly: true },
  { name: "list_site_items", domain: "files", readOnly: true, orgOnly: true },
  { name: "search_site_files", domain: "files", readOnly: true, orgOnly: true },
  // Chats
  { name: "list_chats", domain: "chats", readOnly: true, orgOnly: true },
  { name: "list_chat_messages", domain: "chats", readOnly: true, orgOnly: true },
  { name: "send_chat_message", domain: "chats", readOnly: false, orgOnly: true },
  // Teams
  { name: "list_teams", domain: "teams", readOnly: true, orgOnly: true },
  { name: "list_channels", domain: "teams", readOnly: true, orgOnly: true },
  { name: "list_channel_messages", domain: "teams", readOnly: true, orgOnly: true },
  { name: "send_channel_message", domain: "teams", readOnly: false, orgOnly: true },
  // Meetings (transcripts). Needs admin-consent scopes that are NOT in the defaults — see
  // MS365_EXTRA_SCOPES in the README. Both tools are reachable but will 403 without the grant.
  { name: "list_meeting_transcripts", domain: "meetings", readOnly: true, orgOnly: true },
  { name: "get_meeting_transcript", domain: "meetings", readOnly: true, orgOnly: true },
  // Users
  { name: "get_me", domain: "users", readOnly: true, orgOnly: false },
  { name: "list_users", domain: "users", readOnly: true, orgOnly: true },
  { name: "get_user", domain: "users", readOnly: true, orgOnly: true },
  // Groups
  { name: "list_groups", domain: "groups", readOnly: true, orgOnly: true },
  { name: "get_group", domain: "groups", readOnly: true, orgOnly: true },
  { name: "list_group_members", domain: "groups", readOnly: true, orgOnly: true },
  // Planner
  { name: "list_plans", domain: "planner", readOnly: true, orgOnly: true },
  { name: "list_planner_buckets", domain: "planner", readOnly: true, orgOnly: true },
  { name: "create_planner_bucket", domain: "planner", readOnly: false, orgOnly: true },
  { name: "list_planner_tasks", domain: "planner", readOnly: true, orgOnly: true },
  { name: "get_planner_task", domain: "planner", readOnly: true, orgOnly: true },
  { name: "create_planner_task", domain: "planner", readOnly: false, orgOnly: true },
  { name: "update_planner_task", domain: "planner", readOnly: false, orgOnly: true },
  { name: "update_planner_task_details", domain: "planner", readOnly: false, orgOnly: true },
  // OneNote
  { name: "list_onenote_notebooks", domain: "onenote", readOnly: true, orgOnly: false },
  { name: "list_onenote_sections", domain: "onenote", readOnly: true, orgOnly: false },
  { name: "list_onenote_pages", domain: "onenote", readOnly: true, orgOnly: false },
  { name: "get_onenote_page_content", domain: "onenote", readOnly: true, orgOnly: false },
  { name: "create_onenote_page", domain: "onenote", readOnly: false, orgOnly: false },
  { name: "update_onenote_page_content", domain: "onenote", readOnly: false, orgOnly: false },
  { name: "create_onenote_section", domain: "onenote", readOnly: false, orgOnly: false },
  { name: "create_onenote_notebook", domain: "onenote", readOnly: false, orgOnly: false },
  { name: "copy_onenote_page", domain: "onenote", readOnly: false, orgOnly: false },
  { name: "delete_onenote_page", domain: "onenote", readOnly: false, orgOnly: false },
  // To Do
  { name: "list_todo_lists", domain: "todo", readOnly: true, orgOnly: false },
  { name: "list_todo_tasks", domain: "todo", readOnly: true, orgOnly: false },
  { name: "create_todo_task", domain: "todo", readOnly: false, orgOnly: false },
  { name: "update_todo_task", domain: "todo", readOnly: false, orgOnly: false },
  // Query
  { name: "graph_query", domain: "query", readOnly: false, orgOnly: false },
  // RAG
  { name: "read_document", domain: "rag", readOnly: true, orgOnly: false },
]

export type ToolFilterConfig = {
  readonly presets?: ReadonlyArray<string>
  readonly enabledPattern?: string
  readonly readOnly?: boolean
  readonly orgMode?: boolean
  readonly transport?: TransportMode
  readonly requireDraft?: boolean
}

const DRAFT_BYPASS_TOOLS: ReadonlySet<string> = new Set([
  "send_message",
  "send_reply",
  "send_reply_all",
  "send_forward",
])

export const filterTools = (config: ToolFilterConfig): Set<string> => {
  // Native Set<string> is the returned contract (callers use .has()); the accumulation is functional.
  const allowedDomains =
    config.presets && config.presets.length > 0
      ? new Set<ToolDomain>([...config.presets.flatMap((preset) => PRESETS[preset] ?? []), "auth"])
      : undefined
  const enabledRegex = config.enabledPattern ? new RegExp(config.enabledPattern, "i") : undefined

  const included = (meta: ToolMeta): boolean => {
    if (allowedDomains && !allowedDomains.has(meta.domain)) return false // preset filter
    if (config.readOnly && !meta.readOnly) return false // read-only filter: skip write tools
    if (meta.orgOnly && !config.orgMode) return false // org-mode filter
    if (enabledRegex && !enabledRegex.test(meta.name)) return false // regex filter
    if (meta.transportOnly && config.transport && meta.transportOnly !== config.transport) return false // transport
    if (config.requireDraft && DRAFT_BYPASS_TOOLS.has(meta.name)) return false // require-draft filter
    return true
  }

  return new Set(TOOL_METADATA.filter(included).map((meta) => meta.name))
}
